import { useFormikContext } from "formik";
import { useNavigate } from "react-router-dom";
import { AddressInput } from "@volca/ui/components/ui/address-input";
import { Button } from "@volca/ui/components/ui/button";
import { DateTimePicker } from "@volca/ui/components/ui/date-time-picker";
import { DetailField } from "@volca/ui/components/ui/detail-field";
import { FormInput } from "@volca/ui/components/ui/form-input";
import { Label } from "@volca/ui/components/ui/label";
import { MaskedFormInput } from "@volca/ui/components/ui/masked-form-input";
import { Stack } from "@volca/ui/components/ui/stack";
import { Textarea } from "@volca/ui/components/ui/textarea";
import { SIDEBAR_MODES, SIDEBAR_SECTIONS } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { useBusiness } from "@/core/business";
import { formatDateTimeWithZone } from "@/core/utils/date";
import { dayjs } from "@/core/utils/date";
import { CUSTOMER_STATUS } from "@/customer/constants";
import { getCustomerNameAndPhone } from "@/customer/utils";
import { REFERRAL_COLUMN_TO_STATUS } from "@/referrals/constants";
import type { IReferral } from "@/referrals/referral";
import { FormValues, serviceTitanJobIdMaskOptions } from "./schema";

interface ReferralDetailsFormProps {
  referral: IReferral;
  initialValues: FormValues;
}

export const ReferralDetailsForm = ({
  referral,
  initialValues,
}: ReferralDetailsFormProps) => {
  const navigate = useNavigate();
  const { businessId, selectedBusiness } = useBusiness();
  const { updateSidebar } = useBusinessSettingsSidebar();
  const { values, setFieldValue, getFieldProps, dirty, resetForm } =
    useFormikContext<FormValues>();

  const isServiceTitanEnabled =
    selectedBusiness?.service_titan_enabled || false;

  return (
    <Stack align="start" gap={6} className="w-full py-6 pr-6">
      <FormInput
        label="Services needed"
        placeholder="Services needed"
        {...getFieldProps("service_needed")}
        fullWidth
      />

      <AddressInput
        value={values.address}
        onChange={(value) => setFieldValue("address", value)}
      />
      {referral.status === REFERRAL_COLUMN_TO_STATUS.scheduled && (
        <DateTimePicker
          name="appointment_date"
          label="Appointment date"
          placeholder="mm/dd/yyyy"
          fullWidth
          value={
            values.appointment_date
              ? dayjs(values.appointment_date).toDate()
              : null
          }
          onChange={(date) => {
            void setFieldValue(
              "appointment_date",
              date ? date.toISOString() : null,
            );
          }}
        />
      )}
      {isServiceTitanEnabled && (
        <MaskedFormInput
          name="service_titan_job_id"
          label="ServiceTitan job ID"
          placeholder="ServiceTitan job ID"
          value={values.service_titan_job_id}
          onChange={(value) => setFieldValue("service_titan_job_id", value)}
          maskOptions={serviceTitanJobIdMaskOptions}
          fullWidth
        />
      )}
      <Stack gap={2} className="w-full">
        <Label htmlFor="referral-notes">Notes</Label>
        <Textarea
          id="referral-notes"
          className="min-h-32"
          placeholder="Notes"
          {...getFieldProps("additional_info")}
        />
      </Stack>
      <Stack align="start" direction="row" gap={2}>
        <Button type="submit" variant="secondary" size="sm" disabled={!dirty}>
          Save changes
        </Button>
        <Button
          variant="ghost"
          size="sm"
          type="button"
          disabled={!dirty}
          onClick={() => resetForm({ values: initialValues })}
        >
          Cancel
        </Button>
      </Stack>
      <Stack align="start" gap={6} className="w-full px-4">
        <DetailField
          label="Advocate"
          message={getCustomerNameAndPhone(referral?.advocate)}
          onClick={() => {
            void navigate(
              `/business-settings/${businessId}/customers/${referral?.advocate?.status === CUSTOMER_STATUS.ACTIVE ? "advocates" : "leads"}`,
            );
            updateSidebar({
              section: SIDEBAR_SECTIONS.advocates,
              mode: SIDEBAR_MODES.view,
              selectedItemId: referral?.advocate?.id,
              isOpen: true,
            });
          }}
          extraInfo="Customer who sent the referral (the one you pay out)"
        />
        <DetailField
          label="Referral SMS"
          message={referral.text_message?.body || "No SMS"}
          subMessage={
            referral.created_at
              ? formatDateTimeWithZone(referral.created_at)
              : "No from"
          }
          extraInfo={`Original referral message sent to ${referral.business.name} hotline by advocate`}
        />
      </Stack>
    </Stack>
  );
};
